import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import LandingSection from '../../MyComponents/LandingSections/LandingSection';
import SectionHeading from '../../MyComponents/LandingSections/SectionHeading';
import { featureLinks, featureList, getIntlCount, replaceValues } from '../../common/features';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';
import { getSubscriptionStateAction, SUBSCRIPTION_PLANS, SUBSCRIPTION_STATE_ACTION } from '../../shared/Capabilities.js';

const useStyles = makeStyles(() => ({
    card: {
        boxSizing: 'border-box',
        padding: 'clamp(8px, 1.5vw, 18px)',
        border: `1px solid ${REDESIGN_COLORS.sand}`,
        borderRadius: '24px',
        background: REDESIGN_COLORS.white,
    },
    // The table keeps its natural width and scrolls inside the card on narrow
    // screens; the page itself never scrolls sideways.
    scroller: {
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
    },
    table: {
        width: '100%',
        minWidth: '640px',
        borderCollapse: 'collapse',
        fontSize: '0.92rem',
    },
    head: {
        padding: '16px 14px',
        borderBottom: `1px solid ${REDESIGN_COLORS.sand}`,
        color: REDESIGN_COLORS.muted,
        fontSize: '0.74rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        textAlign: 'left',
    },
    headPlan: {
        textAlign: 'center',
        color: REDESIGN_COLORS.dark,
        fontSize: '1.05rem',
        letterSpacing: '-0.02em',
        textTransform: 'none',
    },
    headCurrent: {
        color: REDESIGN_COLORS.link,
    },
    currentTag: {
        display: 'block',
        marginTop: '4px',
        fontSize: '0.66rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: REDESIGN_COLORS.link,
    },
    rowHead: {
        padding: '16px 14px',
        borderBottom: `1px solid ${REDESIGN_COLORS.sand}`,
        textAlign: 'left',
        verticalAlign: 'top',
        fontWeight: REDESIGN_FONTS.weightStrong,
        color: REDESIGN_COLORS.dark,
    },
    featureLink: {
        color: REDESIGN_COLORS.dark,
        '&:hover': {
            color: REDESIGN_COLORS.link,
        },
    },
    help: {
        display: 'block',
        marginTop: '3px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.8rem',
        fontWeight: 400,
        lineHeight: 1.45,
    },
    helpLine: {
        display: 'block',
    },
    cell: {
        padding: '16px 14px',
        borderBottom: `1px solid ${REDESIGN_COLORS.sand}`,
        textAlign: 'center',
        verticalAlign: 'middle',
        fontWeight: REDESIGN_FONTS.weightStrong,
        color: REDESIGN_COLORS.dark,
    },
    cellCurrent: {
        background: REDESIGN_COLORS.mist,
    },
    tick: {
        color: REDESIGN_COLORS.primary,
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    dash: {
        color: REDESIGN_COLORS.muted,
    },
    lastRow: {
        borderBottom: 0,
    },
}));

const cx = (...names) => names.filter(Boolean).join(' ');

/**
 * Feature-by-feature comparison of the plans, from the same live plan data the
 * pricing cards use. Feature names link to the features page; the signed-in
 * user's own plan column is tinted.
 */
const PlanComparison = ({ subscriptions, profile }) => {
    const classes = useStyles();
    const intl = useIntl();

    if (!subscriptions) return null;

    const plans = [SUBSCRIPTION_PLANS.FREE, SUBSCRIPTION_PLANS.BASIC, SUBSCRIPTION_PLANS.PREMIUM]
        // Teams offers only the base plans; the old page hid Premium there too.
        .filter((type) => !(window.isTeams && type === SUBSCRIPTION_PLANS.PREMIUM))
        .filter((type) => subscriptions[type]);

    const loggedIn = Boolean(profile && profile.loggedIn);
    const isCurrent = (type) => {
        if (!loggedIn) return false;
        const state = getSubscriptionStateAction(profile, subscriptions, type);
        return state === SUBSCRIPTION_STATE_ACTION.ACTIVE || state === SUBSCRIPTION_STATE_ACTION.CANCEL;
    };

    // One resolved feature list per plan; replaceValues mutates, so copy first.
    const valuesFor = {};
    plans.forEach((type) => {
        const list = featureList.map((f) => ({ ...f }));
        replaceValues(subscriptions[type], list);
        valuesFor[type] = Object.fromEntries(list.map((f) => [f.name, f.defaultValue]));
    });

    const cell = (value) => {
        if (typeof value === 'boolean') {
            return value ? (
                <span className={classes.tick} aria-label={intl.formatMessage({ id: 'redesign.pricing.comparison.included' })}>
                    ✓
                </span>
            ) : (
                <span className={classes.dash} aria-label={intl.formatMessage({ id: 'redesign.pricing.comparison.notIncluded' })}>
                    —
                </span>
            );
        }
        if (value === 0) {
            return (
                <span className={classes.dash} aria-label={intl.formatMessage({ id: 'redesign.pricing.comparison.notIncluded' })}>
                    —
                </span>
            );
        }
        return getIntlCount(intl, value);
    };

    return (
        <LandingSection id="comparatie" paddingTop={16} paddingBottom={56}>
            <SectionHeading
                eyebrow={<FormattedMessage id="redesign.pricing.comparison.eyebrow" />}
                title={<FormattedMessage id="redesign.pricing.comparison.title" />}
                lead={<FormattedMessage id="redesign.pricing.comparison.lead" />}
                marginBottom={34}
            />

            <div className={classes.card}>
                <div className={classes.scroller}>
                    <table className={classes.table}>
                        <thead>
                            <tr>
                                <th scope="col" className={classes.head}>
                                    <FormattedMessage id="redesign.pricing.comparison.feature" />
                                </th>
                                {plans.map((type) => (
                                    <th
                                        key={type}
                                        scope="col"
                                        className={cx(classes.head, classes.headPlan, isCurrent(type) && classes.headCurrent)}
                                    >
                                        <FormattedMessage id={`subscriptions.subscription.${type}.name`} />
                                        {isCurrent(type) ? (
                                            <span className={classes.currentTag}>
                                                <FormattedMessage id="redesign.pricing.state.current" />
                                            </span>
                                        ) : null}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {featureList.map((feature, index) => {
                                const isCount = typeof feature.defaultValue !== 'boolean';
                                const last = index === featureList.length - 1;
                                // Counts carry a separate label key; booleans are their own label.
                                const labelId = (isCount ? 'redesign.subscriptions.feature.' : 'subscriptions.feature.') + feature.name + '.name';

                                return (
                                    <tr key={feature.name}>
                                        <th scope="row" className={cx(classes.rowHead, last && classes.lastRow)}>
                                            {featureLinks[feature.name] ? (
                                                <Link to={featureLinks[feature.name]} className={classes.featureLink}>
                                                    <FormattedMessage id={labelId} />
                                                </Link>
                                            ) : (
                                                <FormattedMessage id={labelId} />
                                            )}
                                            <span className={classes.help}>
                                                <FormattedMessage
                                                    id={`subscriptions.feature.${feature.name}.shortSubscriptionDescription`}
                                                    values={{
                                                        b: (chunks) => <strong>{chunks}</strong>,
                                                        li: (chunks) => <span className={classes.helpLine}>{chunks}</span>,
                                                    }}
                                                />
                                            </span>
                                        </th>
                                        {plans.map((type) => (
                                            <td
                                                key={type}
                                                className={cx(classes.cell, isCurrent(type) && classes.cellCurrent, last && classes.lastRow)}
                                            >
                                                {cell(valuesFor[type][feature.name])}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </LandingSection>
    );
};

const mapStateToProps = (state) => ({
    subscriptions: state.mainAppState.subscriptions,
    profile: state.profile,
});

export default connect(mapStateToProps)(PlanComparison);
